// Login.jsx
import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useForm } from "react-hook-form";
import { FaArrowLeft } from "react-icons/fa";
import { useNavigate, Link } from "react-router-dom";
import { Navbar, Footer, Breadcrumbs, Loading } from "../../components/shared";
import { auth } from "../../integrations/Firebase";
import { sendSignInLinkToEmail, signInWithEmailAndPassword } from "firebase/auth";
//import useCheckLoggedin from "../../utils/useCheckLoggedin";
import "../general/General.css";
import { storeDataToIndexDB, getDataFromIndexDB } from "../../utils/dataStore";
import { promptBiometricAuth } from "../../utils/biometricUtils";
import { showErrorToast, showSuccessToast } from "../../utils/Toasts";
import { getLocalizedStrings, getAppLanguage } from "../../utils/translationStrings";
import { useDispatch } from "react-redux";
import { updateUserData } from "../../redux/slice/userSlice";
import { useSelector } from "react-redux";

const Login = () => {
  //useCheckLoggedin();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);
  if (user.email) {
    const redirectTo = localStorage.getItem('postLoginRedirect') || '/user/profile';
    localStorage.removeItem('postLoginRedirect');
    navigate(redirectTo, { replace: true });
  }
  const [language, setLanguage] = useState(getAppLanguage());
  const [strings, setStrings] = useState(getLocalizedStrings(getAppLanguage()));

  useEffect(() => {
    setStrings(getLocalizedStrings(language));
  }, [language]);

  const { register, handleSubmit, formState: { errors }, getValues } = useForm();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [authMethod, setAuthMethod] = useState("emailLink");
  const [emailFromDB, setEmailFromDB] = useState("");
  const [isPasskeyAvailable, setIsPasskeyAvailable] = useState(false);
  const dispatch = useDispatch();
  useEffect(() => {

    const checkPasskeySupport = async () => {
      setLoading(true);
      const email = await getDataFromIndexDB("email");
      if (email) {
        setEmailFromDB(email);
        try {
          const success = await handlePasskeyLogin();
          if (!success) {
            setAuthMethod("emailLink");
            setIsPasskeyAvailable(false);
          }
        } catch (err) {
          setIsPasskeyAvailable(false);
          setAuthMethod("emailLink");
        }
      }
      else {
        // If no email is found in DB, default to email link
        setAuthMethod("emailLink");
        setIsPasskeyAvailable(false);
      }
      setLoading(false);
    };
    checkPasskeySupport();

  }, []);

  const handleFormSubmit = async (data) => {
    setLoading(true);
    setSubmitting(true);
    const { email, password } = data;
    await storeDataToIndexDB("email", email, Number(process.env.REACT_APP_AUTH_SESSION_EXPIRY_TIME));

    if (authMethod === "usernamePassword" && email && password) {
      try {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;
        const idToken = await user.getIdToken();
        await storeDataToIndexDB("authTokenTemp", idToken, 5 * 50 * 1000);
        process.env.REACT_APP_ENV === 'Development' && sessionStorage.setItem('authToken', idToken);
        navigate(`/auth/password/verify?email=${encodeURIComponent(email)}`);
      } catch (error) {
        showErrorToast(strings.errorLoginFail);
      }
    } else if (authMethod === "emailLink" && email) {

      const emailEncoded = encodeURIComponent(email);
      const actionCodeSettings = {
        url: `${process.env.REACT_APP_SIGNUP_COMPLETION_ROOT_URL}?email=${emailEncoded}`,
        handleCodeInApp: true,
      };

      try {
        await sendSignInLinkToEmail(auth, email, actionCodeSettings);
        alert(strings.emailSent);
        showSuccessToast(strings.emailSent);
        navigate("/auth/login/awaiting-link");
      } catch (error) {
        showErrorToast(strings.errorGeneral);
      }
    }
    setSubmitting(false);
    setLoading(false);
  };
  const handlePasskeyLogin = async () => {
    try {
      const { success, user, isBiometricPossible } = await promptBiometricAuth(); // ✅ Calls your shared utility

      if (success) {
        // ✅ Redux update
        dispatch(updateUserData({
          firstName: user.firstName,
          email: user.email,
          roles: user.roles,
          isLoggedIn: true,
        }));
        showSuccessToast(strings.passkeySuccess);
        navigate("/user/profile");
      }
      else if (isBiometricPossible === false) {
        setAuthMethod("emailLink");
        setIsPasskeyAvailable(false);
      }
      else {
        setAuthMethod("emailLink");
        setIsPasskeyAvailable(false);
        showErrorToast(strings.passkeyFail);
      }
      return success;
    } catch (error) {
      setAuthMethod("emailLink");
      setIsPasskeyAvailable(false);
      console.error("Biometric login error caught in handlePasskeyLogin:", error);
      showErrorToast(strings.passkeyFail);
      return false;
    } finally {
      setSubmitting(false); // Reset submitting state
    }
  };

  return (
    <>
      {loading ? (
        <><Navbar />
          <div className="general_parent">
            <div className="general_container">
              <div className="general_body">
                <Breadcrumbs /><br />
                <Loading />
              </div>
            </div>
          </div>
          <Footer />
        </>
      ) : (
        <>
          <Navbar />
          <Helmet>
            <title>ஸ்ரீ பொன்னாச்சி அம்மன் திருக்கோவில்கள் | கணக்கில் உள்நுழைக</title>
            <link rel="canonical" href="/auth/login" />
          </Helmet>
          <div className="general_parent">
            <div className="general_container">
              <div className="general_body">
                <Breadcrumbs />
                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "1rem" }}>
                  <select
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                    style={{ padding: "6px", borderRadius: "4px" }}
                  >
                    <option value="en">English</option>
                    <option value="ta">தமிழ்</option>
                  </select>
                </div>
                <div className="header">
                  <FaArrowLeft onClick={() => navigate("/")} />
                  <h1>{strings.loginHeader}</h1>
                </div>


                <form onSubmit={handleSubmit(handleFormSubmit)} className="auth_form">
                  <div className="auth_element">
                    <input type="hidden" value="individual" {...register("userType")} />
                  </div>

                  <div className="auth_element">
                    <div className="auth_radio">
                      <input
                        type="radio"
                        id="emailLink"
                        name="authMethod"
                        value="emailLink"
                        checked={authMethod === "emailLink"}
                        onChange={() => setAuthMethod("emailLink")}
                      />
                      <label htmlFor="emailLink">{strings.loginViaEmail}</label>
                    </div>

                    {isPasskeyAvailable && (
                      <div className="auth_radio">
                        <input
                          type="radio"
                          id="passkey"
                          name="authMethod"
                          value="passkey"
                          checked={authMethod === "passkey"}
                          onChange={() => setAuthMethod("passkey")}
                        />
                        <label htmlFor="passkey">{strings.loginViaPasskey}</label>
                      </div>
                    )}
                    <div className="auth_radio">
                      <input
                        type="radio"
                        id="usernamePassword"
                        name="authMethod"
                        value="usernamePassword"
                        checked={authMethod === "usernamePassword"}
                        onChange={() => setAuthMethod("usernamePassword")}
                      />
                      <label htmlFor="usernamePassword">{strings.loginViaPassword}</label>
                    </div>
                  </div>
                  {authMethod !== "passkey" && ( // Only show email input if not passkey
                    <div className="auth_element">
                      <label className="auth_label">{strings.emailLabel} 📧</label>
                      <input
                        type="email"
                        id="email"
                        {...register("email", {
                          required: strings.emailRequired,
                          pattern: {
                            value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                            message: strings.emailInvalid,
                          },
                        })}
                        className="auth_input"
                        defaultValue={emailFromDB}
                        disabled={authMethod === "passkey"} // Disable if passkey is selected
                      />
                      {errors.email && <div className="auth_error">{errors.email.message}</div>}
                    </div>
                  )}
                  {authMethod === "usernamePassword" && (
                    <div className="auth_element">
                      <label className="auth_label">{strings.passwordLabel} 🔒</label>
                      <input
                        type="password"
                        id="password"
                        {...register("password", {
                          required: strings.passwordRequired,
                          minLength: {
                            value: 8,
                            message: strings.passwordMinLength,
                          },
                          pattern: {
                            value: /^(?=.*[a-zA-Z])(?=.*[0-9]).{8,}$/,
                            message: strings.passwordPattern,
                          },
                        })}
                        className="auth_input"
                      />
                      {errors.password && <div className="auth_error">{errors.password.message}</div>}
                    </div>
                  )}

                  {authMethod === "passkey" ? (
                    <button
                      type="button"
                      className="auth_submit"
                      onClick={() => handlePasskeyLogin(getValues("email"))}
                      disabled={loading || submitting}
                    >
                      {loading || submitting ? strings.submitLoading : strings.passkeyButton}
                    </button>
                  ) : (
                    <>
                      <button
                        type="submit"
                        disabled={loading || submitting}
                        className={`auth_submit ${loading || submitting ? "disabled_btn" : ""}`}
                      >
                        {loading || submitting
                          ? strings.submitLoading
                          : authMethod === "emailLink"
                            ? strings.loginButton
                            : strings.loginButton}
                      </button>
                      <div style={{ fontFamily: 'sans-serif', fontSize: '16px', lineHeight: '1.6' }}>
                        <p>
                          நீங்கள் உள்நுழைந்து உங்கள் தனிப்பட்ட தகவல்களை வழங்குவதன் மூலம், உங்கள் தரவுகளை
                          சேமிக்கவும் செயல்படுத்தவும் ஒப்புக்கொள்கிறீர்கள். மேலும் விவரங்களுக்கு, தயவுசெய்து
                          எங்கள்{' '}
                          <Link to="/privacy-policy" style={{ color: '#007bff', textDecoration: 'underline' }}>
                            தனியுரிமை கொள்கையை
                          </Link>{' '}
                          பார்க்கவும்.
                        </p>
                      </div>
                    </>
                  )}
                </form>
              </div>
            </div>
          </div>
          <Footer />
        </>
      )}
    </>
  );
};

export default Login;
